// One purchase order (M6): its lines with what arrived and what is still expected, the
// totals from the VAT engine (ex VAT, VAT, with VAT, what can be claimed back), the
// deliveries against it, and what a person can still do: send it themselves (print, save as
// PDF, download) and mark it sent, change the expected date, close it short, cancel it. The
// system sends nothing to the supplier (D-060). A cancelled order reads as cancelled first,
// owes nothing and can't be sent (UI6A-5); a late one, or one that must go out today, says
// so (UI6A-9). A delivery held for a manager (D-066) shows under Deliveries, and the order
// then says it is waiting to be booked, not late (UI6A-102). Paper gets its own layout, the
// one a supplier reads (UI6A-103, printed-order.tsx). Close short is for a sent order that
// stopped arriving; before it is sent, cancelling is the way (UI6A-115).

import { brand } from '@enp/brand';
import type { PoDetail } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Badge,
  Button,
  cn,
  DateField,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  FormField,
  Money,
  PageHeader,
  Skeleton,
  Textarea,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Ban,
  CalendarDays,
  Download,
  PackageCheck,
  Printer,
  Send,
  SquareX,
  TriangleAlert,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { z } from 'zod';
import { meQueryOptions } from '../../../../auth/session.js';
import { orderCsv } from '../../../../purchasing/csv.js';
import { HeldDeliveryList, heldCountText } from '../../../../purchasing/held.js';
import { PrintedOrder } from '../../../../purchasing/printed-order.js';
import { baseQtyText, isOwed, orderTiming, vatClassText } from '../../../../purchasing/figures.js';
import {
  BaseQty,
  KeyHints,
  NoPermission,
  PoStatusBadge,
  PurchasingBreadcrumb,
  Section,
  useCan,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  endOrder,
  heldDeliveriesQuery,
  invalidatePurchasing,
  markOrderSent,
  orderQuery,
  updateOrder,
} from '../../../../purchasing/queries.js';
import {
  discountText,
  LINE_RECEIVING_LABEL,
  packsText,
  PO_STATUS,
  totalsNote,
} from '../../../../purchasing/words.js';
import { downloadText } from '../../../../replay/csv.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { MetaList, useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { plainDateLabel, accountToday } from '../../../../stock/labels.js';
import { actionProblem, Fact, Facts } from '../../../../stock-control/parts.js';

const ACTS = ['send', 'date', 'close', 'cancel'] as const;
type Act = (typeof ACTS)[number];

export const Route = createFileRoute('/_app/purchasing/orders/$orderId')({
  /** `act`: open that action's dialog on arrival (a delivery's "Close … short…" link). */
  validateSearch: z.object({ act: z.enum(ACTS).optional().catch(undefined) }),
  component: OrderPage,
});

type Acting = Act | null;

function who(p: PoDetail['created_by']): string {
  return p?.name ?? (p?.kind === 'staff' ? 'A staff member' : 'An admin user');
}

/** Whether an action is open to this order (the server's `actions`). */
function allowed(po: PoDetail, act: Act): boolean {
  const a = po.actions;
  return act === 'send'
    ? a.mark_sent
    : act === 'date'
      ? a.edit_details
      : act === 'close'
        ? a.close
        : a.cancel;
}

// ---------------------------------------------------------------- dialogs

function ActionDialog({
  po,
  acting,
  held,
  onClose,
}: {
  po: PoDetail;
  acting: Act;
  /** Deliveries against it held for a manager: closing short may drop what they bring. */
  held: readonly string[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const [text, setText] = useState('');
  const [date, setDate] = useState<string | undefined>(po.expected_date ?? undefined);
  const [error, setError] = useState<string>();
  const run = useMutation({
    mutationFn: () => {
      if (acting === 'send') return markOrderSent(po.id, text.trim() || undefined);
      if (acting === 'date') return updateOrder(po.id, { expected_date: date ?? null });
      return endOrder(po.id, acting, text.trim());
    },
    onSuccess: async () => {
      await invalidatePurchasing(queryClient);
      onClose();
      toast.success(
        acting === 'send'
          ? `${po.number} marked sent`
          : acting === 'date'
            ? `${po.number}: expected date saved`
            : acting === 'close'
              ? `${po.number} closed short`
              : `${po.number} cancelled`,
        {
          description:
            acting === 'send'
              ? 'The receiving station now shows it as sent.'
              : acting === 'close'
                ? 'What hasn’t arrived is no longer expected.'
                : acting === 'cancel'
                  ? 'Nothing is owed on it and nothing is expected.'
                  : undefined,
        },
      );
    },
  });
  const failed = run.isError ? actionProblem(run.error, 'It wasn’t saved') : null;
  const needsReason = acting === 'close' || acting === 'cancel';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (needsReason && !text.trim()) {
      setError('Say why, for the record.');
      return;
    }
    run.mutate();
  };
  const outstanding = po.lines.filter((l) => l.receiving === 'none' || l.receiving === 'partial');
  // Name what closing stops expecting (UI6A-115): "Fresh cream: 6 L".
  const stillToCome = outstanding
    .map((l) => `${l.product.name}: ${baseQtyText(l.outstanding_base_qty, l.product.base_uom)}`)
    .join('; ');

  const copy: Record<Act, { title: string; description: ReactNode; button: string }> = {
    send: {
      title: `Mark ${po.number} as sent?`,
      description: `${brand.productName} has not sent anything to ${po.supplier.name} and won’t: send the order yourself (print it, save it as a PDF and email it, or download the CSV). This only records that it went, so everyone knows it is on its way.`,
      button: 'Mark as sent',
    },
    date: {
      title: `When is ${po.number} expected?`,
      description: 'The receiving station and the plan use this date. Clear it if you don’t know.',
      button: 'Save date',
    },
    close: {
      title: `Close ${po.number} short?`,
      description: `What has arrived stays in stock. Still to come: ${stillToCome || 'nothing'}. After closing, ${outstanding.length === 1 ? 'that is' : `those ${formatCount(outstanding.length)} are`} no longer expected and the plan stops counting on ${outstanding.length === 1 ? 'it' : 'them'}. A delivery that arrives anyway is still received, and flagged.`,
      button: 'Close short',
    },
    cancel: {
      title: `Cancel ${po.number}?`,
      description: `Nothing has arrived. Tell ${po.supplier.name} yourself if it was already sent: ${brand.productName} doesn’t contact suppliers.`,
      button: 'Cancel order',
    },
  };
  const c = copy[acting];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent role="alertdialog" className="max-w-lg">
        <DialogForm onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{c.title}</DialogTitle>
            <DialogDescription>{c.description}</DialogDescription>
          </DialogHeader>
          <DialogBody>
            {acting === 'close' && held.length > 0 && (
              <Notice
                tone="warning"
                title={`${held.join(', ')} ${held.length === 1 ? 'is' : 'are'} held, not booked yet`}
              >
                What is still to come may be in {held.length === 1 ? 'it' : 'them'}. Book{' '}
                {held.length === 1 ? 'it' : 'them'} in the held actions first; close short only if
                it really isn’t coming.
              </Notice>
            )}
            {acting === 'date' ? (
              <FormField label="Expected on">
                {(control) => (
                  <DateField
                    {...control}
                    value={date}
                    onChange={setDate}
                    today={today}
                    min={today}
                  />
                )}
              </FormField>
            ) : (
              <FormField
                label={needsReason ? 'Why' : 'How it went (optional)'}
                required={needsReason}
                error={error}
                description={
                  acting === 'send'
                    ? 'For example, “Emailed to orders@… at 09:10”.'
                    : acting === 'close'
                      ? 'For example, “Supplier out of stock; ordered elsewhere”.'
                      : 'For example, “Ordered twice by mistake”.'
                }
              >
                <Textarea
                  autoFocus={needsReason}
                  value={text}
                  maxLength={500}
                  onChange={(e) => {
                    setText(e.target.value);
                    if (error) setError(undefined);
                  }}
                />
              </FormField>
            )}
            {failed && (
              <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
                {failed.explain}
              </Notice>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {/* "Cancel" beside "Cancel the order" would read as two cancels (UI8C-10). */}
              {acting === 'cancel' ? 'Keep it' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant={acting === 'cancel' || acting === 'close' ? 'destructiveSolid' : 'primary'}
              loading={run.isPending}
              autoFocus={!needsReason && acting !== 'date'}
            >
              {c.button}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- the lines

function Lines({ po }: { po: PoDetail }) {
  const owed = isOwed(po.status);
  return (
    <div className="overflow-x-auto rounded-lg border border-border print:overflow-visible">
      <table aria-label="Order lines" className="w-full border-collapse text-body">
        <thead>
          <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
            <th scope="col" className="px-3 py-2">
              Product
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Ordered
            </th>
            <th scope="col" className="px-3 py-2 text-right print:hidden">
              Arrived
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Price per pack
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Ex VAT
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              VAT
            </th>
          </tr>
        </thead>
        <tbody className={cn(!owed && 'text-muted')}>
          {po.lines.map((l) => {
            const unit = l.product.base_uom;
            return (
              <tr key={l.id} className="border-t border-border align-top" data-line={l.product.id}>
                <td className="px-3 py-2">
                  <span className="flex flex-col leading-tight">
                    <span className={cn('font-medium', owed ? 'text-fg' : 'text-muted')}>
                      {l.product.name}
                    </span>
                    <span className="text-xs text-muted">
                      {l.supplier_item.supplier_code ? (
                        <>
                          Their code{' '}
                          <span className="whitespace-nowrap">{l.supplier_item.supplier_code}</span>
                        </>
                      ) : (
                        'No supplier code'
                      )}
                      {l.note && ` · ${l.note}`}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="flex flex-col items-end leading-tight">
                    <span className="whitespace-nowrap font-medium">
                      {packsText(l.qty_packs, l.pack_uom)}
                    </span>
                    <span className="text-xs text-muted">
                      <BaseQty value={l.ordered_base_qty} unit={unit} />
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2 text-right print:hidden">
                  <span className="flex flex-col items-end leading-tight">
                    <BaseQty value={l.received_base_qty} unit={unit} className="font-medium" />
                    <span
                      className={
                        l.receiving === 'over'
                          ? 'text-xs font-medium text-state-warning'
                          : 'text-xs text-muted'
                      }
                    >
                      {l.receiving === 'partial' ? (
                        <>
                          <BaseQty value={l.outstanding_base_qty} unit={unit} />{' '}
                          {owed && po.status !== 'closed' ? 'to come' : 'never came'}
                        </>
                      ) : l.receiving === 'none' && !owed ? (
                        'Cancelled'
                      ) : (
                        LINE_RECEIVING_LABEL[l.receiving]
                      )}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {l.unit_net_cents === null ? (
                    <span className="flex items-center justify-end gap-1 whitespace-nowrap text-sm font-medium text-state-warning">
                      <TriangleAlert aria-hidden className="size-4" />
                      No price
                    </span>
                  ) : (
                    <span className="flex flex-col items-end leading-tight">
                      <Money cents={l.unit_net_cents} className="font-medium" />
                      {l.list_price_cents !== null &&
                        l.discount_bp !== null &&
                        l.discount_bp > 0 && (
                          <span className="whitespace-nowrap text-xs text-muted">
                            List <Money cents={l.list_price_cents} /> less{' '}
                            {discountText(l.discount_bp)}
                          </span>
                        )}
                      {l.price_list_id === null && (
                        <span className="text-xs text-muted">Typed, not from a price list</span>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {l.line_ex_vat_cents === null ? (
                    <span className="text-sm text-muted">—</span>
                  ) : (
                    <Money cents={l.line_ex_vat_cents} className={cn(!owed && 'line-through')} />
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="flex flex-col items-end leading-tight">
                    {l.line_vat_cents === null ? (
                      <span className="text-sm text-muted">—</span>
                    ) : (
                      <Money cents={l.line_vat_cents} className={cn(!owed && 'line-through')} />
                    )}
                    <span className="text-xs text-muted" data-testid="vat-class">
                      {vatClassText(l.vat_rate_code, po.supplier_vat_registered)}
                    </span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Totals({ po }: { po: PoDetail }) {
  const notes = totalsNote(po);
  const owed = isOwed(po.status);
  const struck = cn(!owed && 'text-muted line-through');
  return (
    <div className="flex flex-col items-end gap-2" data-testid="order-totals">
      <dl className="grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-body">
        <dt className="text-muted">Subtotal ex VAT</dt>
        <dd className="text-right">
          <Money cents={po.subtotal_ex_vat_cents} className={cn('text-fg', struck)} />
        </dd>
        <dt className="text-muted">VAT</dt>
        <dd className="text-right">
          <Money cents={po.vat_cents} className={cn('text-fg', struck)} />
        </dd>
        <dt className="border-t border-border pt-1 font-semibold text-fg">
          {owed ? 'Total' : 'Total: not owed'}
        </dt>
        <dd className="border-t border-border pt-1 text-right">
          <Money cents={po.total_cents} className={cn('font-semibold text-fg', struck)} />
        </dd>
        {owed && (
          <>
            <dt className="text-sm text-muted print:hidden">VAT you can claim back</dt>
            <dd className="text-right text-sm print:hidden">
              <Money cents={po.claimable_vat_cents} className="text-muted" />
            </dd>
          </>
        )}
      </dl>
      {!owed && (
        <p className="text-sm font-medium text-fg">Cancelled: nothing is owed on this order.</p>
      )}
      {owed && notes.length > 0 && (
        <ul
          aria-label="About these totals"
          className="flex max-w-lg flex-col gap-1 text-right text-sm"
        >
          {notes.map((n) => (
            <li
              key={n}
              className={n.includes('no price') ? 'font-medium text-state-warning' : 'text-muted'}
            >
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the page

function OrderSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <h1 className="sr-only">Purchase order</h1>
      <span role="status" className="sr-only">
        Loading the order…
      </span>
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function OrderPage() {
  const { orderId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const q = useQuery(orderQuery(orderId));
  const held = useQuery(heldDeliveriesQuery(orderId));
  const heldItems = held.data?.items ?? [];
  const suppliers = useQuery(allSuppliersQuery());
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canOrder = useCan('purchasing.order');
  const [acting, setActing] = useState<Acting>(null);
  const po = q.data;
  useDocumentTitle(po ? po.number : 'Purchase order');

  // Arrived with `act` (from a delivery's flag): that dialog is open, if it is allowed.
  const current: Acting =
    acting ?? (po && search.act && canOrder && allowed(po, search.act) ? search.act : null);
  const closeDialog = () => {
    setActing(null);
    if (search.act) void navigate({ search: {}, replace: true });
  };

  const may = (act: Act) => (po && canOrder && allowed(po, act) ? () => setActing(act) : undefined);
  const owed = po ? isOwed(po.status) : true;
  usePageKeys({
    s: may('send'),
    d: may('date'),
    p: po && owed ? () => window.print() : undefined,
  });

  if (q.isError && !po) {
    return (
      <>
        <PurchasingBreadcrumb trail={[{ to: '/purchasing', label: 'Orders' }]} page="Order" />
        <div className="mt-4 max-w-2xl">
          <ErrorPanel
            error={q.error}
            titleAs="h1"
            what="This order"
            onRetry={() => void q.refetch()}
            extraAction={
              <Button variant="secondary" asChild>
                <Link to="/purchasing">Back to orders</Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }
  if (!po) return <OrderSkeleton />;

  const a = po.actions;
  const anyAction = a.mark_sent || a.edit_details || a.close || a.cancel;
  const status = PO_STATUS[po.status];
  const lead = suppliers.data?.items.find((s) => s.id === po.supplier.id)?.lead_time_days ?? null;
  const timing = orderTiming(po, today, lead, (d) => plainDateLabel(d, today), heldItems.length);
  const blocked = canOrder === false && anyAction;
  // Close short is for a sent order that stopped arriving; an unsent one is cancelled.
  const canClose = a.close && po.status !== 'open';
  const hints: [string, string][] = [
    ...(owed ? ([['p', 'print']] as [string, string][]) : []),
    ...(!blocked && a.edit_details ? ([['d', 'expected date']] as [string, string][]) : []),
    ...(!blocked && a.mark_sent ? ([['s', 'mark sent']] as [string, string][]) : []),
  ];

  return (
    <>
      <PrintedOrder po={po} me={me} />
      <div className="print:hidden">
        <PageHeader
          eyebrow={
            <PurchasingBreadcrumb
              trail={[{ to: '/purchasing', label: 'Orders' }]}
              page={po.number}
            />
          }
          title={
            <span className="flex flex-wrap items-center gap-3">
              <span>
                Purchase order <span className="whitespace-nowrap font-mono">{po.number}</span>
              </span>
              <span className="font-body print:hidden">
                <PoStatusBadge status={po.status} />
              </span>
            </span>
          }
          description={
            <MetaList
              items={[
                po.supplier.name,
                po.branch.name,
                po.expected_date
                  ? `expected ${plainDateLabel(po.expected_date, today)}`
                  : 'no expected date',
                timing && timing.tone !== 'neutral' && (
                  <span
                    key="timing"
                    className={cn(
                      'font-medium',
                      timing.tone === 'danger' ? 'text-state-danger' : 'text-state-warning',
                    )}
                    data-testid="order-timing"
                  >
                    {timing.text}
                  </span>
                ),
              ]}
            />
          }
          actions={
            owed ? (
              <span className="flex flex-wrap justify-end gap-2 print:hidden">
                <Button
                  variant="secondary"
                  icon={<Printer aria-hidden />}
                  aria-keyshortcuts="p"
                  title="Print or save as PDF (P)"
                  onClick={() => window.print()}
                >
                  Print or save as PDF
                </Button>
                <Button
                  variant="secondary"
                  icon={<Download aria-hidden />}
                  onClick={() => downloadText(`${po.number}.csv`, orderCsv(po))}
                >
                  Download CSV
                </Button>
                {!blocked && a.mark_sent && (
                  <Button
                    icon={<Send aria-hidden />}
                    aria-keyshortcuts="s"
                    onClick={() => setActing('send')}
                  >
                    Mark as sent…
                  </Button>
                )}
              </span>
            ) : undefined
          }
        />

        <div className="flex flex-col gap-5">
          {!owed && (
            <>
              <Notice tone="danger" title="Cancelled: nothing is owed">
                Cancelled{' '}
                {po.cancelled_at && (
                  <>
                    <AccountDate value={po.cancelled_at} withTime />{' '}
                  </>
                )}
                by {po.cancelled_by?.name ?? 'someone'}
                {po.cancel_reason ? `: “${po.cancel_reason}”.` : '.'} Nothing is owed and nothing is
                expected. Don’t send it.
              </Notice>
            </>
          )}
          {heldItems.length > 0 && (
            <Notice tone="warning" title={`${heldCountText(heldItems.length)}: not late`}>
              The goods came in at a station, but the server couldn’t book them as sent, so the
              lines don’t count them yet. Don’t chase {po.supplier.name}, order again or close it
              short: a manager books it in the held actions first (see Deliveries below).
            </Notice>
          )}
          {po.status === 'open' && heldItems.length === 0 && (
            <Notice
              tone={timing?.tone === 'neutral' || !timing ? 'warning' : timing.tone}
              title={
                timing && timing.tone !== 'neutral'
                  ? `Not sent yet: ${timing.text}`
                  : 'Not sent yet'
              }
            >
              {brand.productName} doesn’t send orders to suppliers.{' '}
              {canOrder === false
                ? `Someone with the “order stock” permission prints it or saves it as a PDF, sends it to ${po.supplier.name} and marks it sent.`
                : `Print it or save it as a PDF (or download the CSV), send it to ${po.supplier.name} yourself, then mark it sent.`}
              {timing?.tone === 'neutral' && ` ${timing.text}.`}
            </Notice>
          )}
          {po.status === 'closed' && (
            <Notice tone="neutral" title="Closed short">
              {status.means} Closed{' '}
              {po.closed_at && (
                <>
                  <AccountDate value={po.closed_at} withTime />{' '}
                </>
              )}
              by {po.closed_by?.name ?? 'someone'}
              {po.close_reason ? `: “${po.close_reason}”.` : '.'}
            </Notice>
          )}
          {po.status !== 'open' && po.status !== 'cancelled' && po.status !== 'closed' && (
            <p className="text-body text-muted" data-testid="status-means">
              {status.means}
              {po.status === 'sent' && po.sent_at && (
                <>
                  {' '}
                  Marked sent by {who(po.sent_by)}, <AccountDate value={po.sent_at} withTime />
                  {po.sent_note ? `: “${po.sent_note}”` : '.'}
                </>
              )}
            </p>
          )}

          {blocked ? (
            <div>
              <NoPermission>
                Sending, changing, closing or cancelling an order needs the “order stock”
                permission. Ask the owner.
              </NoPermission>
            </div>
          ) : (
            (a.edit_details || canClose || a.cancel || hints.length > 0) && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="mr-auto">
                  <KeyHints keys={hints} />
                </span>
                {a.edit_details && (
                  <Button
                    variant="secondary"
                    icon={<CalendarDays aria-hidden />}
                    aria-keyshortcuts="d"
                    onClick={() => setActing('date')}
                  >
                    Change expected date…
                  </Button>
                )}
                {canClose && (
                  <Button
                    variant="destructive"
                    icon={<SquareX aria-hidden />}
                    onClick={() => setActing('close')}
                    title={
                      heldItems.length > 0
                        ? 'A delivery against it is held: book it before closing short'
                        : undefined
                    }
                  >
                    Close short…
                    {heldItems.length > 0 && (
                      <TriangleAlert aria-hidden className="text-state-warning" />
                    )}
                  </Button>
                )}
                {a.cancel && (
                  <Button
                    variant="destructive"
                    icon={<Ban aria-hidden />}
                    onClick={() => setActing('cancel')}
                  >
                    Cancel order…
                  </Button>
                )}
              </div>
            )
          )}

          <Section id="order-lines" title={`Lines (${po.line_count})`}>
            <Lines po={po} />
            <Totals po={po} />
          </Section>

          <Section id="order-deliveries" title="Deliveries">
            {heldItems.length > 0 && (
              <div className="flex flex-col gap-3 rounded-md border border-state-warning/40 bg-state-warning-subtle p-3">
                <p className="text-body font-semibold text-fg" data-testid="order-held-panel">
                  {heldCountText(heldItems.length)}
                </p>
                <HeldDeliveryList items={heldItems} showOrder={false} />
              </div>
            )}
            {po.receipts.length === 0 ? (
              heldItems.length > 0 ? (
                <p className="text-body text-muted">Nothing booked against this order yet.</p>
              ) : (
                <p className="text-body text-muted">
                  {po.status === 'cancelled'
                    ? 'Cancelled before anything arrived.'
                    : po.status === 'closed'
                      ? 'Closed before anything arrived.'
                      : 'Nothing has arrived yet. Deliveries are received at the receiving station against this order; each one shows here with anything that didn’t match.'}
                </p>
              )
            ) : (
              <ul
                aria-label="Deliveries against this order"
                className="flex flex-col divide-y divide-border"
              >
                {po.receipts.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <span className="flex flex-col leading-tight">
                      <Link
                        to="/purchasing/receipts/$receiptId"
                        params={{ receiptId: r.id }}
                        className="whitespace-nowrap rounded-sm font-mono font-medium text-primary-text underline-offset-2 hover:underline"
                      >
                        {r.number}
                      </Link>
                      <span className="text-sm text-muted">
                        <AccountDate value={r.received_at} withTime /> · {r.location.name} ·{' '}
                        {r.received_by?.name ?? 'Someone at a station'}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 text-sm">
                      <span className="text-muted">
                        {r.line_count} {r.line_count === 1 ? 'line' : 'lines'}
                      </span>
                      {r.flag_count > 0 ? (
                        <Badge tone="warning">
                          <TriangleAlert aria-hidden />
                          {r.flag_count} {r.flag_count === 1 ? 'thing' : 'things'} to check
                        </Badge>
                      ) : (
                        <Badge tone="success">
                          <PackageCheck aria-hidden />
                          As ordered
                        </Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section id="order-record" title="Record">
            <Facts>
              <Fact label="Made">
                <AccountDate value={po.created_at} withTime /> by {who(po.created_by)}
              </Fact>
              <Fact label="From request">
                {po.purchase_request ? (
                  <Link
                    to="/purchasing/requests/$requestId"
                    params={{ requestId: po.purchase_request.id }}
                    className="whitespace-nowrap rounded-sm font-mono text-primary-text underline-offset-2 hover:underline"
                  >
                    {po.purchase_request.number}
                  </Link>
                ) : (
                  'None: made without a request'
                )}
              </Fact>
              <Fact label="Sent">
                {po.sent_at ? (
                  <>
                    <AccountDate value={po.sent_at} withTime /> by {who(po.sent_by)}
                  </>
                ) : po.status === 'cancelled' ? (
                  'Never: cancelled'
                ) : (
                  'Not yet'
                )}
              </Fact>
              <Fact label="Note">{po.note ?? 'None'}</Fact>
            </Facts>
          </Section>
        </div>
      </div>
      {current && (
        <ActionDialog
          po={po}
          acting={current}
          held={heldItems.map((h) => h.number ?? 'A delivery')}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
